import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { ProductType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { SimulationRun_Type } from '@wavingroup/aqora-v2-crate/wavin/simulations/v1/simulations_service_pb';
import { ProductModel } from '~/shared/models/system/ProductModel';
import {
  SystemModel,
  ReservoirModel,
} from '~/shared/models/system/SystemModel';

export interface DefaultMetricTarget {
  resourceName: string;
  productId: string;
  metricType: MetricType;
}

export class DefaultChartMetricsModel {
  private static readonly ACTUATOR_METRIC_TYPES_IDF = [
    MetricType.CALCULATED_VALVE_HEIGHT_PERCENTAGE,
    MetricType.VALVE_HEIGHT_MM,
    MetricType.VALVE_STATE_ON,
    MetricType.TAP_ON,
    MetricType.PUMP_STATE_ON,
  ] as const;

  private static readonly ACTUATOR_METRIC_TYPES = [
    MetricType.VALVE_HEIGHT_MM,
    MetricType.VALVE_STATE_ON,
    MetricType.TAP_ON,
    MetricType.PUMP_STATE_ON,
  ] as const;

  private static readonly FALLBACK_PRIORITY_METRICS = [
    MetricType.WATER_HEIGHT_MM,
    MetricType.PRECIPITATION_MM,
    ...DefaultChartMetricsModel.ACTUATOR_METRIC_TYPES,
  ] as const;

  public static getDefaultMetricsToDisplay({
    system,
    product,
    simulationType,
  }: {
    system: SystemModel;
    product?: ProductModel;
    simulationType?: SimulationRun_Type;
  }): DefaultMetricTarget[] {
    if (product) {
      return DefaultChartMetricsModel.getMetricsForProduct(system, product);
    }
    return DefaultChartMetricsModel.getMetricsForSystem(system, simulationType);
  }

  private static getMetricsForProduct(
    system: SystemModel,
    product: ProductModel,
  ): DefaultMetricTarget[] {
    if (
      DefaultChartMetricsModel.hasMetricType(
        product,
        MetricType.WATER_HEIGHT_MM,
      )
    ) {
      return DefaultChartMetricsModel.getWaterHeightMetrics(system, product);
    }
    return DefaultChartMetricsModel.getFirstThreeMetrics(product);
  }

  private static getMetricsForSystem(
    system: SystemModel,
    simulationType?: SimulationRun_Type,
  ): DefaultMetricTarget[] {
    const polderValves = system.products.filter(
      (product) => product.type === ProductType.SMART_DROP,
    );

    if (polderValves.length > 0) {
      const polderValve =
        polderValves.length === 1
          ? polderValves[0]
          : DefaultChartMetricsModel.findFirstPVInBiggestReservoir(
              system.reservoirs,
              polderValves,
            );

      if (simulationType === SimulationRun_Type.IDF) {
        return DefaultChartMetricsModel.getIdfSystemMetrics(
          system,
          polderValve,
        );
      }

      return DefaultChartMetricsModel.getWaterHeightMetrics(
        system,
        polderValve,
      );
    }

    return DefaultChartMetricsModel.getControllerSystemMetrics(system);
  }

  private static getIdfSystemMetrics(
    system: SystemModel,
    product: ProductModel,
  ): DefaultMetricTarget[] {
    const metrics: DefaultMetricTarget[] = [];

    const waterHeight = DefaultChartMetricsModel.findMetric(
      [product],
      MetricType.CALCULATED_WATER_PERCENTAGE,
    );
    if (waterHeight) metrics.push(waterHeight);

    const precipitation =
      DefaultChartMetricsModel.findPrecipitationInSystem(system);
    if (precipitation) {
      metrics.push(precipitation);
    }

    const actuator = DefaultChartMetricsModel.findFirstAvailableMetric(
      [product],
      DefaultChartMetricsModel.ACTUATOR_METRIC_TYPES_IDF,
    );

    if (actuator) {
      metrics.push(actuator);
    }

    return metrics;
  }

  private static getWaterHeightMetrics(
    system: SystemModel,
    product: ProductModel,
  ): DefaultMetricTarget[] {
    const metrics: DefaultMetricTarget[] = [];

    const waterHeight = DefaultChartMetricsModel.findMetric(
      [product],
      MetricType.WATER_HEIGHT_MM,
    );
    if (waterHeight) metrics.push(waterHeight);

    const precipitation =
      DefaultChartMetricsModel.findPrecipitationInSystem(system);
    if (precipitation) metrics.push(precipitation);

    const actuator = DefaultChartMetricsModel.findFirstAvailableMetric(
      [product],
      DefaultChartMetricsModel.ACTUATOR_METRIC_TYPES,
    );
    if (actuator) metrics.push(actuator);

    return metrics;
  }

  private static findPrecipitationInSystem(
    system: SystemModel,
  ): DefaultMetricTarget | undefined {
    return DefaultChartMetricsModel.findMetric(
      system.products,
      MetricType.PRECIPITATION_MM,
    );
  }

  private static getControllerSystemMetrics(
    system: SystemModel,
  ): DefaultMetricTarget[] {
    const reservoir = DefaultChartMetricsModel.findBiggestReservoirMatching(
      system.reservoirs,
      (r) =>
        r.products.some((p) =>
          DefaultChartMetricsModel.hasMetricType(p, MetricType.WATER_HEIGHT_MM),
        ),
    );

    if (!reservoir) {
      return DefaultChartMetricsModel.getFallbackMetrics(system);
    }

    const metrics: DefaultMetricTarget[] = [];

    const waterHeight = DefaultChartMetricsModel.findMetric(
      reservoir.products,
      MetricType.WATER_HEIGHT_MM,
    );
    if (waterHeight) metrics.push(waterHeight);

    const precipitation =
      DefaultChartMetricsModel.findPrecipitationInSystem(system);
    if (precipitation) metrics.push(precipitation);

    const actuator = DefaultChartMetricsModel.findFirstAvailableMetric(
      reservoir.products,
      DefaultChartMetricsModel.ACTUATOR_METRIC_TYPES,
    );
    if (actuator) metrics.push(actuator);

    return metrics;
  }

  private static getFallbackMetrics(
    system: SystemModel,
  ): DefaultMetricTarget[] {
    for (const priorityMetric of DefaultChartMetricsModel.FALLBACK_PRIORITY_METRICS) {
      const product = system.products.find((p) =>
        DefaultChartMetricsModel.hasMetricType(p, priorityMetric),
      );
      if (product) {
        return DefaultChartMetricsModel.getFirstThreeMetrics(product);
      }
    }

    const productWithMetrics = system.products.find(
      (p) => p.availableMetricChartOptions.length > 0,
    );

    return productWithMetrics
      ? DefaultChartMetricsModel.getFirstThreeMetrics(productWithMetrics)
      : [];
  }

  private static getFirstThreeMetrics(
    product: ProductModel,
  ): DefaultMetricTarget[] {
    return product.availableMetricChartOptions.slice(0, 3).map((option) => ({
      resourceName: option.resourceName,
      productId: product.id,
      metricType: option.type,
    }));
  }

  private static findFirstPVInBiggestReservoir(
    reservoirs: ReservoirModel[],
    polderValves: ProductModel[],
  ): ProductModel {
    const pvsByReservoir = DefaultChartMetricsModel.groupByReservoir(
      reservoirs,
      polderValves,
    );

    const biggestReservoir =
      DefaultChartMetricsModel.findBiggestReservoirMatching(
        [...pvsByReservoir.keys()],
        () => true,
      );

    if (!biggestReservoir) {
      return polderValves[0];
    }

    return pvsByReservoir
      .get(biggestReservoir)!
      .sort((a, b) => a.name.localeCompare(b.name))[0];
  }

  private static groupByReservoir(
    reservoirs: ReservoirModel[],
    products: ProductModel[],
  ): Map<ReservoirModel, ProductModel[]> {
    const map = new Map<ReservoirModel, ProductModel[]>();

    for (const product of products) {
      if (product.reservoirName) {
        const reservoir = reservoirs.find(
          (r) => r.name === product.reservoirName,
        );
        if (reservoir) {
          const existing = map.get(reservoir) ?? [];
          map.set(reservoir, [...existing, product]);
        }
      }
    }

    return map;
  }

  private static findBiggestReservoirMatching(
    reservoirs: ReservoirModel[],
    predicate: (r: ReservoirModel) => boolean,
  ): ReservoirModel | undefined {
    if (reservoirs.length === 0) return undefined;

    return [...reservoirs]
      .filter(predicate)
      .sort((a, b) => Number(b.totalCapacity - a.totalCapacity))[0];
  }

  private static findFirstAvailableMetric(
    products: readonly ProductModel[],
    metricTypes: readonly MetricType[],
  ): DefaultMetricTarget | undefined {
    for (const metricType of metricTypes) {
      const metric = DefaultChartMetricsModel.findMetric(products, metricType);
      if (metric) return metric;
    }
    return undefined;
  }

  private static findMetric(
    products: readonly ProductModel[],
    metricType: MetricType,
  ): DefaultMetricTarget | undefined {
    for (const product of products) {
      const option = product.availableMetricChartOptions.find(
        (opt) => opt.type === metricType,
      );

      if (option) {
        return {
          resourceName: option.resourceName,
          productId: product.id,
          metricType: option.type,
        };
      }
    }

    return undefined;
  }

  private static hasMetricType(
    product: ProductModel,
    metricType: MetricType,
  ): boolean {
    return (
      DefaultChartMetricsModel.findMetric([product], metricType) !== undefined
    );
  }
}
